import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Empty } from 'antd';
import dayjs from 'dayjs';
import { ArrowLeft, Plus } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import {
  OUTCOME, REQ_REASON, SOURCE, STAGE, STAGES, branchById, daysBetween, empById, fmtD, fmtMoney, planStatus, positionById, reqById,
  reqCandidates, reqHired, toDay, type Candidate,
  interviewOverdue, licenseBlocker, needsSchedule, pendingInterview,
} from './hrLogic';
import { Avatar, Badge, ReqStatusTag, empUrl, fmtDateTime, useHrPerms } from './hrUi';
import { CandidateModal, RequisitionModal } from './hrModals';
import CandidateDrawer from './CandidateDrawer';
import { useReqActions } from './Requisitions';

function CandCard({ c, onOpen }: { c: Candidate; onOpen: () => void }) {
  const last = c.history[c.history.length - 1];
  const wait = daysBetween(toDay(last.at), toDay());
  const score = [...c.history].reverse().find(h => h.score)?.score;
  const s = useHr();
  const iv = pendingInterview(c);
  const lic = c.outcome === 'open' && c.stage === 'skill' && !!licenseBlocker(s, c, toDay());
  return (
    <button type="button" onClick={onOpen} className="w-full text-left bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-2.5 hover:border-[#3e91ab] shadow-sm">
      <div className="flex items-center gap-2">
        <Avatar name={c.name} />
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{c.name}</div>
          <div className="text-[11px] text-gray-400 truncate">{SOURCE[c.source]}</div>
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[11px]">
        {score && <span className={`font-semibold ${score >= 7 ? 'text-emerald-600' : score >= 5 ? 'text-amber-600' : 'text-rose-600'}`}>{score}/10</span>}
        {c.offer && <Badge className={c.offer.status === 'accepted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : c.offer.status === 'need_approval' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-sky-50 text-sky-700 border-sky-200'}>
          {{ need_approval: 'Chờ duyệt offer', sent: 'Đã gửi offer', accepted: `Nhận việc ${fmtD(c.offer.startDate)}`, declined: 'Từ chối' }[c.offer.status]}
        </Badge>}
        {iv && <Badge className={interviewOverdue(iv) ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-sky-50 text-sky-700 border-sky-200'}>{interviewOverdue(iv) ? 'Quá giờ PV' : 'PV'} {dayjs(iv.at).format('HH:mm DD/MM')}</Badge>}
        {needsSchedule(c) && <Badge className="bg-amber-50 text-amber-700 border-amber-200">Chưa hẹn PV</Badge>}
        {lic && <Badge className="bg-rose-50 text-rose-700 border-rose-200">Chưa xác minh GP</Badge>}
        {c.outcome === 'open' && <span className={wait > 3 ? 'text-rose-600' : 'text-gray-400'}>{wait ? `chờ ${wait} ngày` : 'hôm nay'}</span>}
      </div>
    </button>
  );
}

/** Chi tiết yêu cầu tuyển dụng: bảng kanban ứng viên theo bước */
export default function RequisitionDetail() {
  const s = useHr();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { canRecruit, canApprove } = useHrPerms();
  const { approve, reject, close } = useReqActions();
  const [cand, setCand] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const today = toDay();
  const r = reqById(s, params.get('id') || undefined);
  if (!r) return <Layout><Empty className="py-16" description="Không tìm thấy yêu cầu"><Button onClick={() => navigate('/jobs-view')}>Về danh sách</Button></Empty></Layout>;
  const pos = positionById(s, r.positionId);
  const cands = reqCandidates(s, r.id);
  const closed = cands.filter(c => c.outcome === 'rejected' || c.outcome === 'withdrawn');
  const hired = reqHired(s, r);
  const st = pos ? planStatus(s, r.branchId, pos.deptId, today) : null;
  const left = daysBetween(today, r.deadline);
  const live = r.status === 'approved';

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <button type="button" onClick={() => navigate('/jobs-view')} className="self-start text-xs text-gray-500 hover:text-[#3e91ab] inline-flex items-center gap-1"><ArrowLeft size={14} /> Yêu cầu tuyển dụng</button>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm space-y-3">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-semibold">{pos?.name} × {r.qty}</h1>
                <ReqStatusTag status={r.status} />
                {r.overPlan && <Badge className="bg-violet-50 text-violet-700 border-violet-200">Vượt định biên</Badge>}
              </div>
              <div className="text-sm text-gray-500">{r.code} · {branchById(s, r.branchId)?.name} · {REQ_REASON[r.reason]}{r.replaceEmpIds?.length ? ` cho ${r.replaceEmpIds.map(id => empById(s, id)?.name).join(', ')}` : ''}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {r.status === 'pending' && canApprove && <><Button type="primary" onClick={() => approve(r)}>Duyệt</Button><Button danger onClick={() => reject(r)}>Từ chối</Button></>}
              {(r.status === 'pending' || r.status === 'rejected') && canRecruit && <Button onClick={() => setEditing(true)}>Sửa & gửi lại</Button>}
              {live && canRecruit && <Button type="primary" icon={<Plus size={14} />} onClick={() => setAdding(true)}>Thêm ứng viên</Button>}
              {live && canRecruit && <Button onClick={() => close(r)}>Đóng yêu cầu</Button>}
              {r.status === 'pending' && canRecruit && <Button onClick={() => close(r, true)}>Hủy</Button>}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <div><div className="text-[11px] text-gray-400">Đã tuyển</div><b>{hired}/{r.qty}</b></div>
            <div><div className="text-[11px] text-gray-400">Khung lương</div>{fmtMoney(r.salaryMin)} – {fmtMoney(r.salaryMax)}</div>
            <div><div className="text-[11px] text-gray-400">Hạn cần người</div><span className={live && left < 0 ? 'text-rose-600 font-medium' : ''}>{fmtD(r.deadline)}{live ? ` (${left < 0 ? `trễ ${-left}` : `còn ${left}`} ngày)` : ''}</span></div>
            <div><div className="text-[11px] text-gray-400">Định biên bộ phận</div>{st ? `${st.actual - st.leaving}/${st.plan} người` : '—'}</div>
            <div><div className="text-[11px] text-gray-400">Đề xuất / duyệt</div><span className="text-xs">{r.createdBy}{r.approvedBy ? ` · ${r.approvedBy}` : ''}</span></div>
          </div>
          {r.note && <div className="text-sm text-gray-600 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2">{r.note}</div>}
          {r.rejectReason && r.status === 'rejected' && <div className="text-sm text-rose-700 bg-rose-50 rounded-lg px-3 py-2">Từ chối: {r.rejectReason}</div>}
          {pos?.license && <div className="text-xs text-amber-700">Vị trí bắt buộc giấy phép hành nghề / chứng chỉ — kiểm tra bản gốc ở vòng chuyên môn.</div>}
        </div>

        <div className="overflow-x-auto pb-2">
          <div className="grid grid-flow-col auto-cols-[minmax(200px,1fr)] gap-3 min-w-[1000px]">
            {STAGES.map(stg => {
              const col = cands.filter(c => c.stage === stg && (c.outcome === 'open' || c.outcome === 'hired'));
              return (
                <div key={stg} className="bg-gray-50 dark:bg-gray-800/40 rounded-xl p-2 flex flex-col gap-2 min-h-[160px]">
                  <div className="flex items-center justify-between px-1">
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: STAGE[stg].color }} />{STAGE[stg].label}</span>
                    <span className="text-xs text-gray-400">{col.length}</span>
                  </div>
                  {col.map(c => <CandCard key={c.id} c={c} onOpen={() => (c.outcome === 'hired' && c.empId ? navigate(empUrl(c.empId)) : setCand(c.id))} />)}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm">
          <button type="button" className="w-full text-left px-4 py-3 text-sm font-semibold flex justify-between" onClick={() => setShowClosed(!showClosed)}>
            <span>Đã loại / ứng viên rút ({closed.length})</span><span className="text-xs text-gray-400 font-normal">{showClosed ? 'Ẩn' : 'Xem'}</span>
          </button>
          {showClosed && (
            <div className="divide-y divide-gray-100 dark:divide-gray-800 border-t border-gray-100 dark:border-gray-800">
              {closed.map(c => (
                <button key={c.id} type="button" onClick={() => setCand(c.id)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/40 flex flex-wrap items-center gap-2">
                  <span className="font-medium">{c.name}</span>
                  <Badge className={OUTCOME[c.outcome].badge}>{OUTCOME[c.outcome].label} · {STAGE[c.stage].short}</Badge>
                  <span className="text-xs text-gray-500">{c.closeReason}</span>
                  <span className="text-[11px] text-gray-400 ml-auto">{c.closedAt ? fmtDateTime(c.closedAt) : ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <CandidateDrawer id={cand} onClose={() => setCand(null)} />
      {adding && <CandidateModal reqId={r.id} onClose={() => setAdding(false)} onSaved={id => setCand(id)} />}
      {editing && <RequisitionModal id={r.id} onClose={() => setEditing(false)} />}
    </Layout>
  );
}
